import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { getCollection } from '../services/firestoreService';
import { useBranch } from '../context/BranchContext';
import HospitalEgresosStatsTab from '../components/HospitalEgresosStatsTab';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const daysBetween = (start, end) => {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return 0;
  return Math.max(0, Math.round((e - s) / (1000 * 60 * 60 * 24)));
};

const isToday = (value) => {
  const d = toDate(value);
  if (!d) return false;
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const d0 = new Date(d);
  d0.setHours(0, 0, 0, 0);
  return d0.getTime() === t.getTime();
};

const COLORS = ['#1A3A5C', '#2CB8A0', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#EC4899'];
const OUTCOME_COLORS = { infeccioso: '#EF4444', 'no infeccioso': '#2CB8A0' };

export const KpiCard = ({ title, value, subtitle, color = 'primary.main', footer }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="h4" fontWeight={800} color={color} sx={{ my: 0.5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" display="block">
          {subtitle}
        </Typography>
      )}
      {footer && (
        <>
          <Divider sx={{ my: 1 }} />
          {footer}
        </>
      )}
    </CardContent>
  </Card>
);

export const ChartCard = ({ title, children, action }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        {action}
      </Box>
      <Box sx={{ flex: 1, minHeight: 260 }}>
        {children}
      </Box>
    </CardContent>
  </Card>
);

export default function HospitalDashboardPage() {
  const { filterByBranch } = useBranch();
  const [hospitalizations, setHospitalizations] = useState([]);
  const [capacities, setCapacities] = useState([]);
  const [sales, setSales] = useState([]);
  const [tiposPrestacion, setTiposPrestacion] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [patients, setPatients] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [h, c, s, tp, eg, pats] = await Promise.all([
          getCollection('hospitalizations'),
          getCollection('capacities'),
          getCollection('Sales'),
          getCollection('tiposPrestacion'),
          getCollection('egresosHospitalarios'),
          getCollection('patients'),
        ]);
        // Se cargan los datos crudos; el filtro por sucursal se aplica en el useMemo `stats`.
        setHospitalizations(h || []);
        setCapacities(c || []);
        setSales(s || []);
        setTiposPrestacion(tp || []);
        setEgresos(eg || []);
        setPatients(pats || []);
      } catch (err) {
        setError('Error cargando datos del hospital.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const stats = useMemo(() => {
    const branchH = filterByBranch(hospitalizations, { includeUnassigned: true });
    const branchCaps = filterByBranch(capacities, { includeUnassigned: true });
    const branchSales = filterByBranch(sales, { includeUnassigned: true });

    const totalCages = branchCaps.reduce(
      (sum, c) => sum + (c.resources?.length || 0),
      0
    );

    const active = branchH.filter((h) => h.status !== 'alta' && h.status !== 'fallecido');
    const discharged = branchH.filter((h) => h.status === 'alta');
    const deaths = branchH.filter((h) => h.status === 'fallecido');

    const occupancyPct =
      totalCages > 0 ? Math.round((active.length / totalCages) * 100) : 0;

    const activeDays = active.reduce((sum, h) => {
      return sum + daysBetween(h.admissionAt, new Date());
    }, 0);
    const avgActiveLos = active.length ? (activeDays / active.length).toFixed(1) : 0;

    const dischargeDays = discharged.reduce((sum, h) => {
      return sum + daysBetween(h.admissionAt, h.altaAt || h.updatedAt);
    }, 0);
    const avgDischargeLos = discharged.length
      ? (dischargeDays / discharged.length).toFixed(1)
      : 0;

    const now = new Date();
    const overdue = active.filter((h) => {
      const t = toDate(h.nextTreatmentAt);
      return t && t < now;
    }).length;

    const statusCounts = active.reduce((acc, h) => {
      const key = h.clinicalStatus || 'sin_estado';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const statusChartData = [
      { name: 'Estable', count: statusCounts.estable || 0 },
      { name: 'Alerta', count: statusCounts.alerta || 0 },
      { name: 'Crítico', count: statusCounts.critico || 0 },
      { name: 'Recup.', count: statusCounts.recuperacion || 0 },
    ];

    const speciesCounts = active.reduce((acc, h) => {
      const key = (h.patientSpecies || 'Sin especie').toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const speciesChartData = Object.entries(speciesCounts)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))
      .sort((a, b) => b.value - a.value);

    const infectiousTypeCounts = active.reduce(
      (acc, h) => {
        if (h.isInfectious) acc.infeccioso += 1;
        else acc.noInfeccioso += 1;
        return acc;
      },
      { infeccioso: 0, noInfeccioso: 0 }
    );

    const infectiousTypeChartData = [
      { name: 'Infeccioso', value: infectiousTypeCounts.infeccioso, color: OUTCOME_COLORS.infeccioso },
      { name: 'No infeccioso', value: infectiousTypeCounts.noInfeccioso, color: OUTCOME_COLORS['no infeccioso'] },
    ];

    const diagnosisCounts = {};
    branchH.forEach((h) => {
      const raw = h.status === 'alta'
        ? h.altaDiagnosticoEgreso || h.primaryDiagnosis
        : h.primaryDiagnosis;
      if (!raw) return;
      const key = raw.trim().toLowerCase();
      diagnosisCounts[key] = (diagnosisCounts[key] || 0) + 1;
    });

    const diagnosisChartData = Object.entries(diagnosisCounts)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    const altasHoy = discharged.filter((h) => isToday(h.altaAt)).length;
    const altasEstimadasHoy = active.filter((h) => isToday(h.fechaAltaEstimada)).length;

    const admissionsByPatient = branchH.reduce((acc, h) => {
      const key = h.patientId || h.patientName || 'desconocido';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const readmittedPatients = Object.values(admissionsByPatient).filter((c) => c > 1).length;
    const uniquePatients = Object.keys(admissionsByPatient).length;
    const readmissionRate = uniquePatients
      ? ((readmittedPatients / uniquePatients) * 100).toFixed(1)
      : 0;

    const mortalityRate = branchH.length
      ? ((deaths.length / branchH.length) * 100).toFixed(1)
      : 0;

    // Set of tipoPrestacion names (lowercase) related to hospitalization
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const hospitalTipoNames = new Set(
      tiposPrestacion
        .filter((tp) => norm(tp.name || '').includes('hospital'))
        .map((tp) => (tp.name || '').trim().toLowerCase())
    );

    // Fallback: also match items with unidadNegocio === 'Hospital' for legacy data
    const isHospitalItem = (it) => {
      const tipo = (it.tipoPrestacion || '').trim().toLowerCase();
      if (tipo && hospitalTipoNames.has(tipo)) return true;
      if (!tipo && norm(it.unidadNegocio || '') === 'hospital') return true;
      return false;
    };

    const hospitalSales = branchSales.filter((sale) =>
      (sale.items || []).some((it) => isHospitalItem(it))
    );

    const hospitalRevenue = hospitalSales.reduce((sum, sale) => {
      const net = Number(sale.subtotalNeto) || Number(sale.subtotal) || 0;
      const sumItems = (sale.items || []).reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0);
      const factor = sumItems > 0 ? net / sumItems : 0;
      const itemsTotal = (sale.items || [])
        .filter((it) => isHospitalItem(it))
        .reduce((acc, it) => acc + (Number(it.subtotal) || 0) * factor, 0);
      return sum + itemsTotal;
    }, 0);

    const revenuePerPatient = branchH.length
      ? Math.round(hospitalRevenue / branchH.length)
      : 0;

    return {
      active: active.length,
      discharged: discharged.length,
      deaths: deaths.length,
      totalCages,
      occupancyPct,
      overdue,
      avgActiveLos,
      avgDischargeLos,
      altasHoy,
      altasEstimadasHoy,
      statusChartData,
      speciesChartData,
      infectiousTypeChartData,
      diagnosisChartData,
      readmissionRate,
      mortalityRate,
      revenue: hospitalRevenue,
      salesCount: hospitalSales.length,
      revenuePerPatient,
    };
  }, [hospitalizations, capacities, sales, tiposPrestacion, filterByBranch]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={800}>
          Dashboard de Hospital
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Indicadores operacionales, clínicos y financieros en tiempo real
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Resumen operacional" />
          <Tab label="Estadísticas de Egresos" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Box>
          <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Hospitalizados"
            value={stats.active}
            subtitle="Pacientes actualmente internados"
            color="primary.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Ocupación"
            value={`${stats.occupancyPct}%`}
            subtitle={`${stats.active} / ${stats.totalCages} camas/jaulas`}
            color="info.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Tratamientos atrasados"
            value={stats.overdue}
            subtitle="Próximos trat. vencidos"
            color={stats.overdue > 0 ? 'warning.main' : 'success.main'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Ingresos Hospital"
            value={`$${stats.revenue.toLocaleString('es-CL')}`}
            subtitle={`${stats.salesCount} ventas asociadas`}
            color="success.main"
            footer={
              <Typography variant="caption" color="text.secondary">
                ${stats.revenuePerPatient.toLocaleString('es-CL')} por paciente hospitalizado
              </Typography>
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Estancia promedio activos"
            value={stats.avgActiveLos}
            subtitle="Días promedio ALOS"
            color="text.primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Estancia alta promedio"
            value={stats.avgDischargeLos}
            subtitle="Días hasta el alta"
            color="text.primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Altas hoy"
            value={stats.altasHoy}
            subtitle={`${stats.altasEstimadasHoy} altas estimadas hoy`}
            color="success.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Mortalidad / Reinternación"
            value={`${stats.mortalityRate}%`}
            subtitle={`Reinternación: ${stats.readmissionRate}%`}
            color={Number(stats.mortalityRate) > 5 ? 'error.main' : 'text.primary'}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title="Estado clínico de pacientes activos">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.statusChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" name="Pacientes" radius={[4, 4, 0, 0]}>
                  {stats.statusChartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Pacientes por especie"
            action={
              <Chip size="small" label={`${stats.speciesChartData.length} especies`} color="primary" />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.speciesChartData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  innerRadius={50}
                  paddingAngle={3}
                  label
                >
                  {stats.speciesChartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title="Pacientes por tipo de hospital">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.infectiousTypeChartData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  innerRadius={50}
                  paddingAngle={3}
                  label
                >
                  {stats.infectiousTypeChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Diagnósticos de alta / prevalencia"
            action={
              <Chip size="small" label="Top 8" color="secondary" />
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={stats.diagnosisChartData}
                layout="vertical"
                margin={{ top: 8, right: 16, left: 40, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" name="Pacientes" radius={[0, 4, 4, 0]}>
                  {stats.diagnosisChartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
      </Box>
      )}

      {tabValue === 1 && (
        <HospitalEgresosStatsTab
          egresos={egresos}
          patients={patients}
          hospitalizations={hospitalizations}
        />
      )}
    </Box>
  );
}
